import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Optional } from '@honda-canada/design-system-react';
import Draggable from 'react-draggable';
import PropTypes from 'prop-types';

import themeStyles from './MobileLayout.styles';
import { DealersListContext } from '../DealersListContext';
import DealerDetailsHeader from '../DealerDetailsModal/DealerDetailsHeader';
import DepartmentTabs from '../DepartmentTabs';
import DealerAccordion from '../DealerAccordion';
import DealerDetailsModal from '../DealerDetailsModal';
import {
  LIST_VIEW_IDENTIFIER,
  MAP_VIEW_IDENTIFIER,
  MAP_ZOOM_DURATION_MODAL_VIEW,
  MAP_ZOOM_LEVEL_MODAL_VIEW,
} from '../../utils/constants';
import GoogleMapRefContext from '../GoogleMapRefContext';
import animateZoomToLevel from '../../utils/googleMap/animateZoomToLevel';
import parseCoordinatesToNumbers from '../../utils/googleMap/parseCoordinatesToNumbers';
import DealerContactQuickLinks from '../DealerContactQuickLinks/DealerContactQuickLinks';
import useConfiguration from '../../hooks/useConfiguration';

const DealerInfoWrapper = themeStyles.apply(Box, 'DealerInfoWrapper');
const ArrowWrapper = themeStyles.apply(Box, 'ArrowWrapper');
const Arrow = themeStyles.apply(Box, 'Arrow');
const DraggableHandler = themeStyles.apply(Box, 'DraggableHandler');

const DEFAULT_HEADER_PADDING = 14;

const DraggableDealerDetails = ({ activeView, isFullScreenModal, setFullScreenModal, variant }) => {
  const draggableNodeRef = useRef(null);
  const [dragPosition, setDragPosition] = useState(null);
  const { selectedDealer, setSelectedDealer, setDealerModalOpen, isDealerModalOpen, setActiveView } =
    useContext(DealersListContext);
  const { showQuickLinks } = useConfiguration();
  const { current: mapRef } = useContext(GoogleMapRefContext);

  useEffect(() => {
    if (isDealerModalOpen) {
      setFullScreenModal(true);
    }
  }, []);

  useEffect(() => {
    if (!isDealerModalOpen) {
      setFullScreenModal(false);
    }
  }, [isDealerModalOpen, setFullScreenModal]);

  const onStart = () => {
    setDealerModalOpen(true);
    mapRef?.panTo?.(parseCoordinatesToNumbers(selectedDealer?.location?.coordinates));
    animateZoomToLevel(mapRef, MAP_ZOOM_LEVEL_MODAL_VIEW, MAP_ZOOM_DURATION_MODAL_VIEW);
    window.scrollTo(0, 0);
  };

  const onStop = () => {
    setDragPosition({ x: 0, y: 0 });
    setDealerModalOpen(!isFullScreenModal);
    setFullScreenModal(!isFullScreenModal);
  };

  const onCloseClick = () => {
    if (activeView === LIST_VIEW_IDENTIFIER) {
      setActiveView(MAP_VIEW_IDENTIFIER);
    }
    setSelectedDealer(null);
  };

  return (
    <Draggable
      bounds="parent"
      handle=".handle"
      nodeRef={draggableNodeRef}
      allowAnyClick
      axis="y"
      position={dragPosition}
      onStart={onStart}
      onStop={onStop}
    >
      <DealerInfoWrapper
        data-testid={isFullScreenModal ? 'cy-full-screen-draggable-modal' : 'cy-collapsed-draggable-modal'}
        isBottomPanel={!isFullScreenModal}
        ref={draggableNodeRef}
        headerPadding={isDealerModalOpen && DEFAULT_HEADER_PADDING}
      >
        <DraggableHandler className="handle">
          <ArrowWrapper>
            <Arrow
              isArrowDown={isFullScreenModal}
              data-testid={isFullScreenModal ? 'cy-draggable-arrow-down' : 'cy-draggable-arrow-up'}
            />
          </ArrowWrapper>
        </DraggableHandler>
        {isDealerModalOpen ? (
          <DealerDetailsModal
            dealer={selectedDealer}
            isFullScreenModal={isFullScreenModal}
            setFullScreenModal={setFullScreenModal}
            modalHeight={isFullScreenModal ? 'auto' : ''}
          >
            <DepartmentTabs dealer={selectedDealer} variant={variant} />
            <Optional when={!showQuickLinks}>
              <DealerAccordion productLines={selectedDealer?.productLines} />
            </Optional>
            <Optional when={showQuickLinks}>
              <DealerContactQuickLinks
                dealerCode={selectedDealer?.code}
                serviceBookingUrl={selectedDealer?.serviceBookingUrl}
              />
            </Optional>
          </DealerDetailsModal>
        ) : (
          <DealerDetailsHeader dealer={selectedDealer} onCloseClick={onCloseClick} />
        )}
      </DealerInfoWrapper>
    </Draggable>
  );
};

DraggableDealerDetails.propTypes = {
  activeView: PropTypes.string,
  isFullScreenModal: PropTypes.bool,
  setFullScreenModal: PropTypes.func,
  variant: PropTypes.string,
};

export default DraggableDealerDetails;
